import { ChangeDetectionStrategy, Component, OnInit, input, output, inject, signal } from '@angular/core';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { AddTransactionDraft, Bucket, Transaction } from '../../types';
import { TransactionService } from '../../services/transaction.service';
import { BucketService } from '../../services/bucket.service';
import { DateTime } from 'luxon';
import { AnalyticsService } from '../../services/analytics.service';
import { EVENT_CREATED_TRANSACTION } from '../../analytics/events';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiCurrencyInputComponent } from '../../ui/currency-input/currency-input.component';
import { UiDatePickerComponent } from '../../ui/date-picker/date-picker.component';
import { UiRadioGroupComponent } from '../../ui/radio-group/radio-group.component';
import { UiInputDirective, UiTextareaDirective } from '../../ui/input/input.directive';
import { LabelTypeaheadComponent } from '../../labels/label-typeahead/label-typeahead.component';
import { UiButtonDirective } from '../../ui/button/button.directive';

@Component({
  selector: 'app-add-transaction',
  templateUrl: './add-transaction.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    UiFieldComponent,
    UiCurrencyInputComponent,
    UiDatePickerComponent,
    UiRadioGroupComponent,
    UiInputDirective,
    LabelTypeaheadComponent,
    UiTextareaDirective,
    UiButtonDirective,
  ],
})
export class AddTransactionComponent implements OnInit {
  private analytics = inject(AnalyticsService);
  bucketService = inject(BucketService);
  transactionService = inject(TransactionService);

  readonly bucket = input<Bucket | undefined>();
  readonly transactionAdded = output<Bucket | undefined>();
  readonly createTransactionMutation = injectMutation(() => this.transactionService.createTransactionMutationOptions());

  readonly loading = signal(false);

  transaction: AddTransactionDraft = {
    type: 'EXPENSE',
    date: DateTime.local().toISODate(),
    bucketId: null,
    labelIds: [],
  };

  ngOnInit() {
    this.transaction.bucketId = this.bucket()?.id ?? null;
  }

  addTransactionFormSubmit(form: NgForm) {
    this.loading.set(true);
    this.createTransactionMutation.mutate(this.toTransactionPayload(), {
      onSuccess: () => {
        this.transactionAdded.emit(this.bucket());
        this.analytics.sendEvent(EVENT_CREATED_TRANSACTION);
        form.resetForm({
          type: 'EXPENSE',
          date: DateTime.local().toISODate(),
          bucketId: this.bucket()?.id ?? null,
          labelIds: [],
        });
      },
      onSettled: () => this.loading.set(false),
    });
  }

  private toTransactionPayload(): Partial<Transaction> {
    return {
      ...this.transaction,
      bucketId: this.transaction.bucketId ?? undefined,
    };
  }
}
